import { useEffect, useState } from 'react';
import { Header } from '../../components/Header';
import { SEO } from '../../components/SEO';
import { ClickAnimation } from '../../components/ClickAnimation';
import { motion, AnimatePresence } from 'framer-motion';
import { getImageUrl } from '../../lib/imageUrls';
import { PROFILE_STORAGE_KEY, PROFILE_TEXT } from '../../content/profileCopy';

// Profile data configuration
const DEFAULT_PROFILE = {
  intro: PROFILE_TEXT.intro,
  headline: PROFILE_TEXT.headline,
  subtitle: PROFILE_TEXT.subtitle,
  bio: PROFILE_TEXT.bio,
  experience: [
    "Rapid, Gothenburg\nExecutive Creative Director\n2020 - Present",
    "AKQA, London / Gothenburg / Stockholm\nCreative Director\n2013 - 2020",
    "Amaze, Manchester\nLead Designer\n2011 - 2013",
    "Code Computerlove, Manchester\nDesigner\n2006 - 2011"
  ],
  awards: [
    "IAB Creative Showcase Winner - Nissan IDx",
    "TheWebby Awards - Honoree Nissan IDx",
    "TheFWA - AKQA.com",
    "TheFWA - Site of the day - Lexus Europe",
    "TheFWA - Mobile of the day",
    "TheFWA - Lexus IS Interactive film",
    "TheFWA - The million Mile Test Drive",
    "Awwwards - Site of the Day - Lexus Europe",
    "Tasty Website Awards - Lexus Europe",
    "Big Chip - Lexus Europe Best Use of Digital",
    "Big Chip - Best Use of Visual Design - Seaworld",
    "Big Chip - HMV",
    "Graduated with a Distincion  – Manchester College"
  ],
  publications: [
    "Computer Arts",
    "Formula 1",
    "Pixels of Fury",
    "The Drive",
    "Jalopnik",
    "The Drum",
    "Top Gear Magazine"
  ],
  conferences: [
    "Beyond the Bulldog podcast",
    "3GO podcast",
    "GrowthTank 2024",
    "NVIDIA GTC21",
    "DokuTech",
    "REDO Design Conference"
  ],
  socialLinks: [
    { name: "Instagram", url: "https://www.instagram.com/besbujupi" },
    { name: "LinkedIn", url: "https://www.linkedin.com/in/besbujupi/" },
    { name: "X", url: "https://x.com/besbujupi" },
    { name: "Behance", url: "https://www.behance.net/besbujupi" }
  ],
  heroImages: [
    "/interactiveHero/image-1.jpg",
    "/interactiveHero/image-2.jpg",
    "/interactiveHero/image-2b.png",
    "/interactiveHero/image-3.jpg",
    "/interactiveHero/image-4.jpg",
    "/interactiveHero/image-5.jpg",
    "/interactiveHero/image-6.jpg",
    "/interactiveHero/image-7.jpg",
    "/interactiveHero/image-8.jpg",
    "/interactiveHero/image-9.jpg",
    "/interactiveHero/image-10.png"
  ]
};

const CONFERENCE_LINKS: Record<string, string> = {
  "Beyond the Bulldog podcast": "https://www.youtube.com/watch?v=pdWJ1PucCpE",
  "3GO podcast": "https://www.youtube.com/watch?v=m-UxoT89tqY",
  "GrowthTank 2024": "https://www.youtube.com/watch?v=weoWYrA9MyY",
  "NVIDIA GTC21": "https://www.nvidia.com/en-us/on-demand/session/gtcspring21-e32374/?playlistId=playList-ce2850f8-1c4c-42ab-831c-e8986d2473b1",
};

function getLocalProfile() {
  try {
    const val = localStorage.getItem(PROFILE_STORAGE_KEY);
    return val ? JSON.parse(val) : DEFAULT_PROFILE;
  } catch {
    return DEFAULT_PROFILE;
  }
}

export const Profile = (): JSX.Element => {
  const [profile, setProfile] = useState(DEFAULT_PROFILE);
  const [clickAnimation, setClickAnimation] = useState<{ visible: boolean; position: { x: number; y: number } }>({
    visible: false,
    position: { x: 0, y: 0 }
  });

  useEffect(() => {
    setProfile(getLocalProfile());
  }, []);

  // Handle click animation
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      // Only trigger animation for left mouse button clicks
      if (event.button !== 0) return;
      
      // Prevent conflicts with existing click handlers by using a small delay
      requestAnimationFrame(() => {
        setClickAnimation({
          visible: true,
          position: { x: event.clientX, y: event.clientY }
        });

        // Hide animation after it completes (1.08 seconds based on 27 frames at 25fps)
        setTimeout(() => {
          setClickAnimation(prev => ({ ...prev, visible: false }));
        }, 1080);
      });
    };

    // Use capture phase to ensure we catch clicks before other handlers
    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, []);

  const socialLinks = profile.socialLinks;
  const experience = profile.experience || DEFAULT_PROFILE.experience;
  const awards = profile.awards;
  const publications = profile.publications;
  const conferences = profile.conferences;
  const intro = profile.intro || DEFAULT_PROFILE.intro;
  const headline = profile.headline ?? DEFAULT_PROFILE.headline;
  const subtitle = profile.subtitle || DEFAULT_PROFILE.subtitle;

  const renderHeadline = () => {
    const text = headline ?? intro;
    const dotIndex = text.indexOf('.');
    if (dotIndex === -1) {
      return text;
    }
    return (
      <>
        {text.slice(0, dotIndex)}
        <span className="text-[#0048FF]">{text[dotIndex]}</span>
        {text.slice(dotIndex + 1)}
      </>
    );
  };

  // Collapsible state for each section
  const [experienceOpen, setExperienceOpen] = useState(true);
  const [awardsOpen, setAwardsOpen] = useState(false);
  const [publicationsOpen, setPublicationsOpen] = useState(false);
  const [conferencesOpen, setConferencesOpen] = useState(false);


  return (
    <>
      <SEO 
        title={PROFILE_TEXT.seo.profileTitle}
        description={PROFILE_TEXT.seo.profileDescription}
        url="https://bes.art/profile"
      />
      <ClickAnimation 
        isVisible={clickAnimation.visible} 
        position={clickAnimation.position} 
      />
      <main className="relative w-full min-h-screen">
        {/* Fixed gradient background */}
        <div className="fixed inset-0 bg-white dark:bg-gradient-to-br dark:from-[#0a0a0a] dark:to-[#2d2d2d] -z-10" />
        
        <div className="relative w-full max-w-[1442px] min-h-screen pb-[600px] lg:pb-[800px] px-4 lg:px-8 mx-auto">
          <Header />
          
          
          {/* Profile Layout */}
          <div className="pt-[180px] lg:pt-[197px] relative z-40">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
              {/* Left side - Image (sticky on desktop) */}
              <div className="lg:w-1/2 lg:sticky lg:top-[200px] lg:h-fit flex justify-center lg:justify-start lg:pl-8">
                {/* Mobile: Image first */}
                <div className="mb-8 lg:mb-0 w-full lg:w-[82.5%] lg:max-w-lg">
                  <motion.div
                    initial={{ clipPath: 'inset(100% 0 0 0)' }}
                    animate={{ clipPath: 'inset(0 0 0 0)' }}
                    transition={{ 
                      duration: 1.2, 
                      ease: [0.83, 0, 0.17, 1],
                      delay: 0.2
                    }}
                    className="overflow-hidden rounded-lg"
                  >
                    <img 
                      src={getImageUrl("/projects/bb25.jpg")} 
                      alt="Bes Bujupi" 
                      className="w-full h-auto object-cover"
                    />
                  </motion.div>
                </div>
              </div>

              {/* Right side - Content */}
              <div className="lg:w-1/2 space-y-6 md:space-y-[40px]">
                {/* Headline */}
                <div className="mb-1 md:mb-1.5">
                  <div className="text-3xl md:text-5xl font-thin [font-family:'ItemsTextTrial-Thin',Helvetica] text-black dark:text-white">
                    {renderHeadline()}
                  </div>
                </div>

                {/* Tagline */}
                <header className="mb-8 md:mb-[60px]">
                  <div className="flex items-center gap-4">
                    <p className="text-base lg:text-xl [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white">{subtitle}</p>
                  </div>
                  <div className="flex flex-wrap gap-4 md:gap-8 mt-4 md:mt-8">
                    <a 
                      href="mailto:hello@besbujupi.com"
                      className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
                    >
                      Email
                    </a>
                    {socialLinks.map((link, index) => (
                      <a 
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm md:text-base text-black dark:text-white hover:opacity-70 transition-opacity [font-family:'ItemsTextTrial-Light',Helvetica] font-light"
                      >
                        {link.name}
                      </a>
                    ))}
                  </div>
                </header>

                <pre className="text-base lg:text-xl leading-5 lg:leading-7 [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white whitespace-pre-line">
                  {profile.bio}
                </pre>

              {/* Experience section */}
              <motion.section 
                className="space-y-6 md:space-y-[40px]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center justify-between gap-2 cursor-pointer select-none" onClick={() => setExperienceOpen(o => !o)}>
                  <h2 className="text-xl md:text-[28px] [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white">Experience</h2>
                  <span className="text-xl md:text-2xl font-bold text-[#0048FF]">
                    {experienceOpen ? '−' : '+'}
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  {experienceOpen && (
                    <motion.ul 
                      className="space-y-4 text-sm md:text-base [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {experience.map((exp, index) => {
                        // Parse experience string safely
                        const parts = exp.split('\n');
                        const company = parts[0];
                        const role = parts[1];
                        const period = parts[2];
                        
                        return (
                          <motion.li 
                            key={index} 
                            className="whitespace-pre-line" 
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
                          >
                            <span className="font-bold text-lg md:text-xl">{company}</span>
                            {role && <><br />{role}</>}
                            {period && <><br />{period}</>}
                          </motion.li>
                        );
                      })}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.section>

              {/* Awards section */}
              <motion.section 
                className="space-y-6 md:space-y-[40px]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center justify-between gap-2 cursor-pointer select-none" onClick={() => setAwardsOpen(o => !o)}>
                  <h2 className="text-xl md:text-[28px] [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white">Awards</h2>
                  <span className="text-xl md:text-2xl font-bold text-[#0048FF]">
                    {awardsOpen ? '−' : '+'}
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  {awardsOpen && (
                    <motion.ul 
                      className="space-y-4 text-sm md:text-base [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {awards.map((award, index) => (
                        <motion.li 
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {award}
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.section>

              {/* Publications section */}
              <motion.section 
                className="space-y-6 md:space-y-[40px]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center justify-between gap-2 cursor-pointer select-none" onClick={() => setPublicationsOpen(o => !o)}>
                  <h2 className="text-xl md:text-[28px] [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white">Publications</h2>
                  <span className="text-xl md:text-2xl font-bold text-[#0048FF]">
                    {publicationsOpen ? '−' : '+'}
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  {publicationsOpen && (
                    <motion.ul 
                      className="space-y-4 text-sm md:text-base [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {publications.map((publication, index) => (
                        <motion.li 
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
                        >
                          {publication}
                        </motion.li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.section>

              {/* Conferences section */}
              <motion.section 
                className="space-y-6 md:space-y-[40px]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="flex items-center justify-between gap-2 cursor-pointer select-none" onClick={() => setConferencesOpen(o => !o)}>
                  <h2 className="text-xl md:text-[28px] [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white">Public Speaking</h2>
                  <span className="text-xl md:text-2xl font-bold text-[#0048FF]">
                    {conferencesOpen ? '−' : '+'}
                  </span>
                </div>
                <AnimatePresence mode="wait">
                  {conferencesOpen && (
                    <motion.ul 
                      className="space-y-4 text-sm md:text-base [font-family:'ItemsTextTrial-Light',Helvetica] font-light text-black dark:text-white"
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      {conferences.map((conference, index) => {
                        const label = typeof conference === 'string' ? conference : conference.name;
                        const url =
                          (typeof conference === 'string' ? CONFERENCE_LINKS[conference] : conference.url);

                        return (
                          <motion.li 
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.4, delay: index * 0.05, ease: [0.22, 1, 0.36, 1] }}
                          >
                            {url ? (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline decoration-[#0048FF] decoration-1 underline-offset-[3px] hover:opacity-70 transition-opacity"
                              >
                                {label}
                              </a>
                            ) : (
                              label
                            )}
                          </motion.li>
                        );
                      })}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </motion.section>

              {/* Logos image - same width as content column (half screen on desktop), always visible */}
              <img
                src={getImageUrl('/_images/BB-Logos.png')}
                alt="Speaking and partner logos"
                className="w-full rounded-lg"
              />
              </div>
            </div>
          </div>

        </div>
      </main>
    </>
  );
};